import { maskReplayAttribute } from "./posthogReplayPresentation.js"
import { gunzipSync, gzipSync, strFromU8 } from "fflate"

// Capture-time masking and bounded decoding protect encoded and uncompressed DOM replay.
const PRIVATE_ATTRIBUTE = /^(?:data-(?:page|props|bootstrap|initial|app-data|application-data)(?:$|-.*)|.*(?:token|secret|password|ssn|dob|birth|email|phone|income|applicant|borrower).*|srcdoc|value|checked|selected)$/i
const URL_ATTRIBUTE = /^(?:href|src|action|formaction)$/i

export const PRIVATE_REPLAY_CONTROL_SELECTOR = 'input[type="checkbox"],input[type="radio"]'

const MAX_REPLAY_BYTES = 2 * 1024 * 1024

function decodeReplayField(value) {
  if (typeof value !== "string" || value.length < 18 || value.length > MAX_REPLAY_BYTES) throw new TypeError("Invalid replay gzip size")
  const bytes = Uint8Array.from(value, (char) => {
    if (char.charCodeAt(0) > 255) throw new TypeError("Invalid replay binary string")
    return char.charCodeAt(0)
  })
  if (bytes[0] !== 31 || bytes[1] !== 139 || bytes[2] !== 8 || bytes[3] !== 0) throw new TypeError("Invalid replay gzip header")
  const footer = new DataView(bytes.buffer, bytes.byteOffset + bytes.length - 8, 8)
  const declaredSize = footer.getUint32(4, true)
  if (declaredSize > MAX_REPLAY_BYTES) throw new TypeError("Oversize replay gzip")
  // Allocate only the checked/capped declared size plus one so truncation is detectable.
  const decoded = gunzipSync(bytes, { out: new Uint8Array(declaredSize + 1) })
  if (decoded.length > MAX_REPLAY_BYTES || decoded.length !== declaredSize) throw new TypeError("Invalid replay gzip length")
  const checked = gzipSync(decoded)
  const checkedFooter = new DataView(checked.buffer, checked.byteOffset + checked.length - 8, 8)
  if (checkedFooter.getUint32(0, true) !== footer.getUint32(0, true)) throw new TypeError("Invalid replay gzip checksum")
  return JSON.parse(strFromU8(decoded))
}

function decodeReplaySnapshot(event) {
  if (!Object.hasOwn(event, "cv")) return event
  if (event.cv !== "2024-10") throw new TypeError("Unknown replay codec")
  const { cv: _cv, ...decoded } = event
  if (event.type === 2) {
    decoded.data = decodeReplayField(event.data)
    if (!decoded.data || typeof decoded.data !== "object" || Array.isArray(decoded.data)) throw new TypeError("Invalid replay snapshot")
  } else if (event.type === 3 && [0, 8].includes(event.data?.source)) {
    const fields = event.data.source === 0 ? ["texts", "attributes", "removes", "adds"] : ["adds", "removes"]
    decoded.data = { ...event.data }
    for (const key of fields) {
      if (event.data.source === 8 && event.data[key] === undefined) continue
      const value = decodeReplayField(event.data[key])
      if (!Array.isArray(value)) throw new TypeError("Invalid replay mutation")
      decoded.data[key] = value
    }
  } else throw new TypeError("Invalid compressed replay type")
  return decoded
}

export function sanitizeReplayDomSnapshot(event, sanitizeUrl) {
  event = decodeReplaySnapshot(event)
  if (event?.type === 3 && event.data?.source === 8) {
    // CSS additions can carry private URLs or generated text; retain valid removals only.
    const { replace: _replace, replaceSync: _replaceSync, ...data } = event.data
    event = { ...event, data: { ...data, adds: [] } }
  }
  if (event?.type === 3 && event.data?.source === 13) {
    // Both operations are optional in rrweb; omit CSS values and custom property names.
    const { set: _set, remove: _remove, ...data } = event.data
    event = { ...event, data }
  }
  if (event?.type === 3 && event.data?.source === 15 && event.data.styles !== undefined) {
    if (!Array.isArray(event.data.styles)) throw new TypeError("Invalid adopted styles")
    const styles = event.data.styles.map((style) => {
      if (!style || typeof style !== "object" || Array.isArray(style) || !Number.isFinite(style.styleId) || !Array.isArray(style.rules)) throw new TypeError("Invalid adopted style")
      return { styleId: style.styleId, rules: [] }
    })
    event = { ...event, data: { ...event.data, styles } }
  }
  if (event?.type === 3 && event.data?.source === 5) {
    const { text: _text, isChecked: _isChecked, ...data } = event.data
    return { ...event, data }
  }
  const visit = (value, attributes = false, tagName = "") => {
    if (Array.isArray(value)) return value.map((entry) => visit(entry))
    if (value === null || typeof value !== "object") {
      if (attributes && value !== null) throw new TypeError("Malformed replay attributes")
      return value
    }
    return Object.fromEntries(Object.entries(value).flatMap(([key, entry]) => {
      if (attributes) {
        // rrweb style mutations may contain structured CSS; omit inline CSS entirely.
        if (["style", "_csstext", "csstext"].includes(key.toLowerCase()) || PRIVATE_ATTRIBUTE.test(key)) return []
        if (typeof entry === "string" && /^\s*[\[{]/.test(entry)) return []
        if (entry !== null && typeof entry === "object") throw new TypeError("Malformed replay attribute value")
        if (/^rr_(?:width|height|left|top|scrollleft|scrolltop)$/.test(key.toLowerCase())) {
          return typeof entry === "string" && /^-?\d{1,7}(?:\.\d{1,5})?(?:px|%)?$/.test(entry) ? [[key, entry]] : []
        }
        if (key.toLowerCase() === "rr_position" && ["static", "relative", "absolute", "fixed", "sticky"].includes(entry)) return [[key, entry]]
        if (key.toLowerCase() === "rr_display" && ["none", "block", "inline", "inline-block", "flex", "grid"].includes(entry)) return [[key, entry]]
        const safe = maskReplayAttribute(key, typeof entry === "string" && URL_ATTRIBUTE.test(key) ? sanitizeUrl(entry) : entry, { tagName: tagName || (key === "src" ? "img" : "link") })
        return safe ? [[key, safe]] : []
      }
      if (key === "attributes" && value.tagName === "meta" && entry && typeof entry === "object") {
        const { content: _content, ...metadata } = entry
        return [[key, visit(metadata, true)]]
      }
      if (key === "textContent" && value.isStyle === true) return [[key, ""]]
      return [[key, visit(entry, key === "attributes", value.tagName)]]
    }))
  }
  return visit(event)
}
